import { inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import {
  BehaviorSubject,
  filter,
  fromEvent,
  interval,
  Subject,
  Subscription,
  tap,
} from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { isPlatformBrowser } from '@angular/common';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Auth, getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'google_token';
  private platformId = inject(PLATFORM_ID);

  public authenticationSubject = new BehaviorSubject<boolean>(false);
  public userSubject = new BehaviorSubject<any>(null);
  private subscriptions: Subscription[] = [];

  // Thêm Subject để kiểm tra trước khi đăng xuất
  logoutConfirmationSubject = new Subject<boolean>();
  logoutConfirmation$ = this.logoutConfirmationSubject.asObservable();
  private logoutEventSubject = new Subject<void>();
  logoutEvent$ = this.logoutEventSubject.asObservable();

  private auth: Auth;
  constructor(private ngZone: NgZone) {
    this.auth = getAuth();
    // Setup Firebase auth state listener để tự động update token khi Firebase refresh
    this.setupFirebaseAuthListener();
  }

  private setupFirebaseAuthListener(): void {
    if (this.isBrowser()) {
      onAuthStateChanged(this.auth, async (user: User | null) => {
        if (user) {
          try {
            const newToken = await user.getIdToken(true);
            this.saveToken(newToken, true);
          } catch (error) {
            console.error('Error getting refreshed token:', error);
            this.logOut();
          }
        } else {
          if (this.isLocalStorageAvailable()) {
            localStorage.removeItem(this.TOKEN_KEY);
          }
          this.authenticationSubject.next(false);
          this.userSubject.next(null);
        }
      });
    }
  }

  setupTokenMonitoring() {
    // Hủy tất cả subscription hiện tại trước khi tạo mới
    this.unsubscribeAll();

    // Kiểm tra token mỗi giây
    this.subscriptions.push(
      interval(1000).subscribe(() => {
        const isCurrentlyLoggedIn = this.isloggedIn();
        if (this.authenticationSubject.value !== isCurrentlyLoggedIn) {
          this.ngZone.run(() => {
            this.authenticationSubject.next(isCurrentlyLoggedIn);

            if (!isCurrentlyLoggedIn) {
              this.userSubject.next(null);
            } else {
              this.userSubject.next(this.getUserDetail());
            }
          });
        }
      })
    );

    // Lắng nghe sự kiện storage khi token thay đổi trong tab khác
    if (this.isBrowser()) {
      this.subscriptions.push(
        fromEvent<StorageEvent>(window, 'storage')
          .pipe(
            filter((event) => event.key === this.TOKEN_KEY),
            tap(() => this.load())
          )
          .subscribe()
      );
    }
  }

  async load() {
    try {
      let loggedIn = this.isloggedIn();
      this.authenticationSubject.next(loggedIn);

      if (!loggedIn) {
        this.userSubject.next(null);
        return;
      }

      const userDetail = this.getUserDetail();
      this.userSubject.next(userDetail);
    } catch (e) {
      console.error('Error loading auth data:', e);
    }
  }

  // Hàm kiểm tra có phải môi trường browser không
  isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  // Tạo hàm utility để xử lý token
  private extractUserFromToken(token: string) {
    if (!token) return null;

    try {
      const decodedToken: any = jwtDecode(token);
      if (!decodedToken) return null;

      return {
        name: decodedToken.name || '',
        email: decodedToken.email || '',
        picture: decodedToken.picture || '',
        initialAvatar: this.getLastNameInitial(decodedToken.name || ''),
      };
    } catch (error) {
      console.error('Error extracting user from token:', error);
      return null;
    }
  }

  // Sửa hàm getUserDetail để dùng utility function
  getUserDetail() {
    const token = this.getToken();
    return token ? this.extractUserFromToken(token) : null;
  }

  async handleFirebaseAuthResult(): Promise<boolean> {
    try {
      const result = await FirebaseAuthentication.getIdToken();

      if (result.token) {
        this.saveToken(result.token);
        return true;
      }
      return false;
    } catch (error: any) {
      if (error.message?.includes('No user is signed in')) {
        return false;
      }

      console.error('Unexpected Firebase auth error:', error);
      return false;
    }
  }

  // Thêm hàm để force refresh token từ Firebase
  async refreshToken(): Promise<string | null> {
    try {
      const user = this.auth.currentUser;
      if (user) {
        const newToken = await user.getIdToken(true);
        this.saveToken(newToken);
        return newToken;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  }

  async isUserSignedIn(): Promise<boolean> {
    try {
      const currentUser = await FirebaseAuthentication.getCurrentUser();
      return !!currentUser.user;
    } catch (error) {
      console.error('Error checking user sign in status:', error);
      return false;
    }
  }
  saveToken(idToken: string, skipStateUpdate: boolean = false) {
    if (!idToken) {
      return;
    }

    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(this.TOKEN_KEY, idToken);
    } else {
      return;
    }

    // Chỉ update state nếu không phải refresh token
    if (!skipStateUpdate) {
      const userDetail = this.extractUserFromToken(idToken);
      if (userDetail) {
        this.ngZone.run(() => {
          this.authenticationSubject.next(true);
          this.userSubject.next(userDetail);
        });
      }
    }
  }

  getToken = (): string | null => {
    if (this.isLocalStorageAvailable()) {
      const token = localStorage.getItem(this.TOKEN_KEY);
      if (token && token !== 'null' && token !== 'undefined') {
        return token;
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  getDecodedToken() {
    const token = this.getToken();
    if (token) {
      try {
        return jwtDecode(token);
      } catch (error) {
        console.error('Error decoding token:', error);
        return null;
      }
    }
    return null;
  }

  isloggedIn = (): boolean => {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    // Chỉ kiểm tra format token, không force logout khi hết hạn
    // Để Firebase tự động refresh token
    try {
      const decoded = jwtDecode(token);
      return !!decoded;
    } catch (error) {
      // Token không hợp lệ, xóa khỏi storage
      if (this.isLocalStorageAvailable()) {
        localStorage.removeItem(this.TOKEN_KEY);
      }
      return false;
    }
  };

  // Yêu cầu xác nhận đăng xuất
  requestLogoutConfirmation() {
    this.logoutConfirmationSubject.next(true);
  }

  // Hủy xác nhận đăng xuất
  cancelLogoutConfirmation() {
    this.logoutConfirmationSubject.next(false);
  }

  // Xóa token khi logOut
  async logOut() {
    try {
      if (this.isLocalStorageAvailable()) {
        localStorage.removeItem(this.TOKEN_KEY);
      }

      // Reset các subject
      this.authenticationSubject.next(false);
      this.userSubject.next(null);
      await FirebaseAuthentication.signOut();

      this.logoutEventSubject.next();
      // this.router.navigate(['/home']);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  getLastNameInitial(fullName: string): string {
    if (!fullName || fullName === 'Chưa cập nhật') return '';
    const nameParts = fullName.trim().split(' ');
    const lastName = nameParts[nameParts.length - 1];
    return lastName ? lastName.charAt(0).toUpperCase() : '';
  }

  private isLocalStorageAvailable(): boolean {
    try {
      const available = this.isBrowser() && !!window && !!window.localStorage;
      return available;
    } catch (e) {
      console.log('localStorage check error:', e);
      return false;
    }
  }

  // Thêm phương thức để hủy tất cả subscription
  private unsubscribeAll() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }
}
